import React, { useState, useEffect, useCallback } from 'react';
import {
  FiMail,
  FiSend,
  FiEye,
  FiMousePointer,
  FiXCircle,
  FiUserX,
  FiArrowUpRight,
  FiCheckCircle,
} from 'react-icons/fi';
import { getAuthContext } from '../../utils/jwt';
import { toast } from 'react-toastify';
import { BASE_URL } from '../../utils/constants';

// Type-cast Feather icons to ensure compatibility with React's component props
const FiMailComponent = FiMail as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiSendComponent = FiSend as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiEyeComponent = FiEye as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiMousePointerComponent =
  FiMousePointer as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
  }>;
const FiXCircleComponent = FiXCircle as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiUserXComponent = FiUserX as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiArrowUpRightComponent =
  FiArrowUpRight as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
  }>;
const FiCheckCircleComponent = FiCheckCircle as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

// Interface for analytics data from API - matches the exact response with 8 fields
interface AnalyticsData {
  openRate: number;
  unsubscribed: number;
  bounced: number;
  clickRate: number;
  delivered: number;
  sent: number;
  opened: number;
  clicked: number;
}

// TopCards component renders a grid of cards displaying key email campaign metrics
const TopCards: React.FC = () => {
  // Manages the selected time range
  const [selectedRange, setSelectedRange] = useState<'PT1H' | 'PT24H' | 'PT168H' | 'PT720H' | 'PT8760H'>('PT720H');

  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Base URL for API calls

  // Function to get date range based on selected filter
  const getDateRange = useCallback(() => {
    const now = new Date();
    let from: Date;

    switch (selectedRange) {
      case 'PT1H':
        from = new Date(now);
        from.setHours(from.getHours() - 1);
        break;
      case 'PT24H':
        from = new Date(now);
        from.setDate(from.getDate() - 1);
        break;
      case 'PT168H':
        from = new Date(now);
        from.setDate(from.getDate() - 7);
        break;
      case 'PT720H':
        from = new Date(now);
        from.setDate(from.getDate() - 30);
        break;
      case 'PT8760H':
        from = new Date(now);
        from.setFullYear(from.getFullYear() - 1);
        break;
      default:
        from = new Date(now);
        from.setHours(from.getHours() - 1);
        break;
    }

    return {
      from: from.toISOString(),
      to: now.toISOString(),
    };
  }, [selectedRange]);

  // Function to fetch analytics data
  const fetchAnalytics = useCallback(async () => {
    const { token, tenantId, userId } = getAuthContext();

    if (!token || !tenantId) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { from, to } = getDateRange();

      // Construct the API URL with query parameters
      const url = `${BASE_URL}/v1/analytics/cards?tenantId=${tenantId}&grain=${selectedRange}&from=${from}&to=${to}`;


      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: AnalyticsData = await response.json();

      setAnalyticsData(data);
    } catch (err: any) {
      // console.error('❌ Failed to fetch analytics:', err);
      setError(err.message || 'Failed to load analytics data');
      toast.error('Failed to load analytics data');

      // Set fallback data to all zeros
      setAnalyticsData({
        sent: 0,
        delivered: 0,
        openRate: 0,
        clickRate: 0,
        bounced: 0,
        unsubscribed: 0,
        opened: 0,
        clicked: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [getDateRange, selectedRange]);

  // Fetch analytics when component mounts or range changes
  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Render loading state
  if (loading) {
    return (
      <div>
        <div className="flex justify-end mb-4">
          <div className="h-10 w-40 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, index) => (
            <div key={index} className="bg-white rounded-md p-4 border shadow-sm animate-pulse">
              <div className="flex justify-between">
                <div className="h-4 bg-gray-200 rounded w-20"></div>
                <div className="h-10 w-10 bg-gray-200 rounded"></div>
              </div>
              <div className="h-8 bg-gray-200 rounded w-24 mt-2"></div>
              <div className="h-4 bg-gray-200 rounded w-32 mt-2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Render error state
  if (error && !analyticsData) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4 text-center">
        <p className="text-red-600">Unable to load analytics data</p>
        <button
          onClick={() => fetchAnalytics()}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  // Use real data or fallback (all zeros) - ensure we always have a valid object
  const data = analyticsData || {
    sent: 0,
    delivered: 0,
    openRate: 0,
    clickRate: 0,
    bounced: 0,
    unsubscribed: 0,
    opened: 0,
    clicked: 0,
  };

  // Calculate percentage for display
  const openRatePercentage = (data.openRate || 0) * 100;
  const clickRatePercentage = (data.clickRate || 0) * 100;

  // Calculate percentage changes (all 0 since fallback is 0)
  const calculateChange = (current: number, previous: number) => {
    if (previous === 0) return 0;
    return ((current - previous) / Math.abs(previous)) * 100;
  };

  // Format metrics with changes - 8 cards
  const metrics = [
    {
      title: 'Total Sent',
      value: (data.sent || 0).toLocaleString(),
      change: calculateChange(data.sent || 0, 0),
      icon: <FiMailComponent className="text-orange-500" />,
      iconBg: 'bg-[#FFF1EC]',
      subtitle: (data.sent || 0) > 0 ? 'created this month' : 'No data available',
    },
    {
      title: 'Delivered',
      value: (data.delivered || 0).toLocaleString(),
      change: calculateChange(data.delivered || 0, 0),
      icon: <FiSendComponent className="text-teal-500" />,
      iconBg: 'bg-[#E0F8FB]',
      subtitle: (data.delivered || 0) > 0 ? 'Currently running' : 'No data available',
    },
    {
      title: 'Opened',
      value: (data.opened || 0).toLocaleString(),
      change: calculateChange(data.opened || 0, 0),
      icon: <FiEyeComponent className="text-blue-500" />,
      iconBg: 'bg-[#E8F4FD]',
      subtitle: (data.opened || 0) > 0 ? 'Total opens' : 'No data available',
    },
    {
      title: 'Clicked',
      value: (data.clicked || 0).toLocaleString(),
      change: calculateChange(data.clicked || 0, 0),
      icon: <FiMousePointerComponent className="text-green-500" />,
      iconBg: 'bg-[#ECFDF5]',
      subtitle: (data.clicked || 0) > 0 ? 'Total clicks' : 'No data available',
    },
    {
      title: 'Open Rate',
      value: `${openRatePercentage.toFixed(1)}%`,
      change: calculateChange(openRatePercentage, 0),
      icon: <FiCheckCircleComponent className="text-indigo-500" />,
      iconBg: 'bg-[#EEF2FF]',
      subtitle: 'vs last month',
    },
    {
      title: 'Click Rate',
      value: `${clickRatePercentage.toFixed(1)}%`,
      change: calculateChange(clickRatePercentage, 0),
      icon: <FiMousePointerComponent className="text-emerald-500" />,
      iconBg: 'bg-[#D1FAE5]',
      subtitle: 'vs last month',
    },
    {
      title: 'Bounced',
      value: (data.bounced || 0).toLocaleString(),
      change: calculateChange(data.bounced || 0, 0),
      icon: <FiXCircleComponent className="text-purple-500" />,
      iconBg: 'bg-[#F3E8FF]',
      subtitle: (data.bounced || 0) > 0 ? 'created this month' : 'No bounces',
    },
    {
      title: 'Unsubscribed',
      value: (data.unsubscribed || 0).toLocaleString(),
      change: calculateChange(data.unsubscribed || 0, 0),
      icon: <FiUserXComponent className="text-red-500" />,
      iconBg: 'bg-[#FFECEC]',
      subtitle: (data.unsubscribed || 0) > 0 ? 'Currently running' : 'No unsubscribes',
    },
  ];

  return (
    <div>
      {/* Filter Dropdown */}
      <div className="flex justify-end mb-4">
        <select
          className='border rounded p-2 text-[14px] border-[#E1EAF0] bg-[#F5F8FA] text-[#3F5265] cursor-pointer hover:bg-[#E1EAF0] transition-colors'
          value={selectedRange}
          onChange={(e) => setSelectedRange(e.target.value as 'PT1H' | 'PT24H' | 'PT168H' | 'PT720H' | 'PT8760H')}
        >
          <option value='PT1H'>Last 1 Hour</option>
          <option value='PT24H'>Last 24 Hours</option>
          <option value='PT168H'>Last 7 Days</option>
          <option value='PT720H'>Last 30 Days</option>
          <option value='PT8760H'>Last 1 Year</option>
        </select>
      </div>

      {/* Cards Grid */}
      <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4'>
        {metrics.map((metric, index) => (
          <div key={index} className='bg-white rounded-md p-4 border shadow-sm flex flex-col'>
            <div className='flex justify-between'>
              <h3 className='text-sm font-semibold text-gray-500'>{metric.title}</h3>
              <span className={`p-2 ${metric.iconBg} rounded`}>
                {metric.icon}
              </span>
            </div>
            <p className='text-2xl font-semibold text-[#3F5265]'>{metric.value}</p>
            <div className='flex items-center mt-1'>
              {metric.change !== 0 ? (
                <span className={`text-[12px] ${metric.change > 0 ? 'text-[#07A26F]' : 'text-red-500'} flex items-center`}>
                  {metric.change > 0 ? (
                    <FiArrowUpRightComponent className='mr-1' />
                  ) : (
                    <span className='mr-1'>↓</span>
                  )}
                  {Math.abs(Math.round(metric.change))}%
                  <span className='text-[#657584] text-[12px] ml-1'>{metric.subtitle}</span>
                </span>
              ) : (
                <span className='text-[12px] text-[#657584]'>{metric.subtitle}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TopCards;